'use client';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { isProductConfigurable } from '@/util/isProductConfigurable';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { forMatLabel } from '../lib/format-label';
import { useProductFullDetailStore } from '../store/use-product-full-detail-store';
import type { SwatchDataValue } from '../types/data';
import type { Value, Variant } from '../types/product';
import { ProductRoot } from '../types/product';
import { getInStockSize } from './get-sorted-color';
import SizeChartDialogLazyLoad from './size-chart-dialog-lazy-load';

const SizeChartNoFlash = dynamic(() => import('@/components/ProductOptions/sizeChartNoFlash'), { ssr: false });

// 将 custom size 放到第一个
function reorderCustomizableRadioValues(values: SwatchDataValue[]): SwatchDataValue[] {
  // 创建一个新数组，避免修改原始数组
  const newValues = [...values];

  // 查找 customized_size 项
  const customizedSizeIndex = newValues.findIndex(item => item?.label === 'customized_size');

  // 如果找到 customized_size
  if (customizedSizeIndex !== -1) {
    // 移除 customized_size 项
    const customizedSize = newValues.splice(customizedSizeIndex, 1)[0];

    // 将 customized_size 项插入到数组开头
    newValues.unshift(customizedSize);
  }

  return newValues;
}

type SizeOptionProps = {
  item: {
    uid: string;
    label: string;
    values: SwatchDataValue[];
  };
  isWeddingShoes: boolean;
  show_attribute: any;
  outOfStockVariants: any;
  product: ProductRoot;
  showSizeChart?: boolean;
  variants: Variant[];
  handleClick: any;
  optionSpecs: any;
  hasAvailableProduct: boolean;
  getIsOutOfStock: (formField: string, valueIndex: string) => boolean;
  shippingType: 'Dresses' | 'Wedding Dresses';
};

export const SizeOptions = ({
  item,
  isWeddingShoes,
  show_attribute,
  outOfStockVariants,
  product,
  showSizeChart = true,
  variants,
  handleClick,
  optionSpecs,
  hasAvailableProduct,
  getIsOutOfStock,
  shippingType,
}: SizeOptionProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const isConfigurable = isProductConfigurable(product);

  // 将 custom size 放到第一个
  const reorderedValues = reorderCustomizableRadioValues(item.values);

  let finalSizeValues = reorderedValues;

  if (isConfigurable && hasAvailableProduct) {
    const inStockSize = getInStockSize({
      values: reorderedValues as Value[],
      variants,
      attribute_code: (item as any)?.attribute_code,
    });

    finalSizeValues = inStockSize;
  }

  const form = useFormContext();
  const { watch } = form;
  const allFormData = watch();
  const sizeData = watch('Size');

  const sizeChart =
    item?.label?.toLowerCase().indexOf('size') > -1 ? (
      <SizeChartNoFlash
        sizeLabel={item.label}
        showAttribute={product.show_attribute}
        selectedValue={{
          default_label: allFormData[forMatLabel(item.label)]?.label,
        }}
      />
    ) : null;

  return (
    <>
      <FormField
        key={item.uid}
        control={form.control}
        name={`${forMatLabel(item.label)}`}
        render={({ field }) => (
          <FormItem className='bg-white  px-2.5 py-[15px] lg_px-0 lg_py-0'>
            <FormLabel
              className='option_title__x4i8y  mb-2.5 flex items-center justify-between '
              id={`${forMatLabel(item.label)}`}
            >
              <span>
                <span className='text-base uppercase leading-5'>{item?.label}: </span>
                <span className='ml-2.5 text-[15px] leading-5 text-80583F'>
                  {allFormData[forMatLabel(item?.label)]?.label?.replace('customized_size', '')}
                </span>
              </span>

              {showSizeChart && (
                <span
                  className='cursor-pointer text-sm leading-5 underline lg_hover_text-80583F'
                  onClick={() => setIsOpen(true)}
                >
                  View Size Chart
                </span>
              )}
              <SizeChartDialogLazyLoad
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                isWeddingShoes={isWeddingShoes}
                shippingType={shippingType}
                show_attribute={show_attribute}
              />
            </FormLabel>
            <FormMessage />

            <FormControl>
              <RadioGroup
                suppressHydrationWarning
                onValueChange={value => {
                  const selectedOption = item.values.find(v => v.value_index.toString() === value);

                  if (selectedOption) {
                    field.onChange({
                      value_index: value,
                      label: selectedOption.label,
                      price: selectedOption.price,
                      option_id: selectedOption?.option_id?.toString() || (item as any).attribute_id,
                    });

                    const couldSelect = optionSpecs.includes(value);

                    // handleClick(couldSelect, value, 1)
                  }
                }}
                value={field?.value?.value_index ?? ''}
                className='flex flex-wrap gap-2.5 lg_gap-x-[5px]'
              >
                {finalSizeValues.map(option => (
                  <SizeRadioItem
                    key={option.uid}
                    item={option}
                    outOfStockVariants={outOfStockVariants}
                    optionSpecs={optionSpecs}
                    hasAvailableProduct={hasAvailableProduct}
                    isOutOfStock={getIsOutOfStock(forMatLabel(item.label), option.value_index)}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </FormItem>
        )}
      />

      {showSizeChart && (
        <div
          className={cn(
            !allFormData[forMatLabel(item.label)]?.label ||
              allFormData[forMatLabel(item.label)]?.label === 'customized_size'
              ? ''
              : 'lg_mb-2.5',
            'bg-white',
          )}
        >
          {sizeChart}
        </div>
      )}
    </>
  );
};

const SizeRadioItem = ({
  item,
  isOutOfStock,
  optionSpecs,
  hasAvailableProduct,
}: {
  item: SwatchDataValue;
  outOfStockVariants: any;
  optionSpecs: any;
  hasAvailableProduct: boolean;
  isOutOfStock: boolean;
}) => {
  // Add this line to get form context
  const { watch } = useFormContext();
  const selectedSize = watch('Size');

  // const inStock = optionSpecs.includes(item.value_index.toString());

  // const isOutOfStock = outOfStockVariants.flat().includes(item.value_index);

  const isSelected = selectedSize?.value_index === item.value_index.toString();
  const setShowMobileCustomlizeDialog = useProductFullDetailStore(state => state.setShowMobileCustomlizeDialog);

  return (
    <FormItem className={cn('flex items-center space-y-0', isOutOfStock && hasAvailableProduct && 'hidden opacity-50')}>
      <FormControl>
        <RadioGroupItem
          value={item?.value_index?.toString()}
          className='peer sr-only'
          suppressHydrationWarning
        />
      </FormControl>
      <FormLabel
        className={cn(
          'tile_root__gozBD hover:border-primary hover:border  hover:border-solid relative flex  min-h-[36px] flex-none cursor-pointer items-center justify-center border-bgtheme px-1',
          isSelected ? 'bg-80583F text-white' : 'bg-f8f3ef',
          item.label === 'customized_size' ? 'min-w-[172px] lg_min-w-[123px]' : 'min-w-[81px] lg_min-w-[59px]',
          isOutOfStock && 'overflow-hidden opacity-50',
          'rounded-[3px]',
        )}
        onClick={() => {
          if (item.label === 'customized_size') {
            setShowMobileCustomlizeDialog(true);
          }
        }}
      >
        {isOutOfStock && <div className='absolute left-px top-[46%] z-[999] h-px w-full rotate-45 bg-white' />}
        <div className='flex items-center gap-x-[5px] text-[15px]'>
          {item.label === 'customized_size' ? (
            <>
              <span>Custom Size</span>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='46'
                height='17'
                fill='none'
                viewBox='0 0 46 17'
                className='lg_hidden'
              >
                <path
                  fill='#fff'
                  d='m.518 11.828 8.115-6v6z'
                />
                <rect
                  width='38'
                  height='16'
                  x='7.633'
                  y='0.828'
                  fill='#fff'
                  rx='8'
                />
                <path
                  fill='#BE9983'
                  d='M14.68 5.928v-1.5h3.756v1.5zm0 3.312V7.812h3.636V9.24zm-1.104-4.812h1.656v8.4h-1.656zm8.342 4.488h1.812l2.736 3.912h-2.028zM20.01 4.428h1.716v8.4H20.01zm1.044 1.464V4.428h1.776q.996 0 1.692.336t1.068.936.372 1.404q0 .792-.372 1.404-.372.6-1.068.936t-1.692.336h-1.776V8.412h1.668q.456 0 .78-.144.336-.156.516-.432.18-.288.18-.684t-.18-.672a1.1 1.1 0 0 0-.516-.432 1.8 1.8 0 0 0-.78-.156zm7.501 6.936v-1.452h4.236v1.452zm0-6.948V4.428h4.236V5.88zm0 3.204V7.656h3.996v1.428zm-1.104-4.656h1.656v8.4h-1.656zm8.135 8.4v-1.452h4.236v1.452zm0-6.948V4.428h4.236V5.88zm0 3.204V7.656h3.996v1.428zm-1.104-4.656h1.656v8.4h-1.656z'
                />
              </svg>
            </>
          ) : (
            item.label
          )}
        </div>

        {item.label === 'customized_size' && (
          <div className='absolute -top-1.5 right-2 hidden lg_block'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='38'
              height='20'
              fill='none'
              viewBox='0 0 38 20'
            >
              <rect
                width='38'
                height='16'
                className={isSelected ? 'fill-f8f3ef' : 'fill-BE9983'}
                rx='8'
              />
              <path
                className={isSelected ? 'fill-BE9983' : 'fill-f8f3ef'}
                d='M5.984 12V4.08h5.208v1.464H7.604v1.98h2.94v1.464h-2.94V12zm13.106 0h-1.824l-2.052-2.796h-1.188V12h-1.62V4.08h2.976q1.248 0 2.052.708t.804 1.848q0 .744-.372 1.344-.36.588-1.02.912zm-5.064-6.492v2.268h1.356q.564 0 .876-.312.324-.312.324-.828 0-.504-.336-.816-.324-.312-.864-.312zM20.082 12V4.08h5.328v1.464h-3.708v1.608h2.724v1.38h-2.724v2.004h3.852V12zm6.879 0V4.08h5.328v1.464H28.58v1.608h2.724v1.38H28.58v2.004h3.852V12z'
              />
              <path
                className={isSelected ? 'fill-f8f3ef' : 'fill-BE9983'}
                d='m15 14.96 8.567.082-4.243 4.243z'
              />
            </svg>
          </div>
        )}
      </FormLabel>
    </FormItem>
  );
};
